import _ from "lodash";
import React, { useRef, useState, useCallback, useMemo } from "react";
import classes from "./DeadlinesPopover.scss";
import classNames from "classnames";

import { Popover, Avatar, List, Tooltip } from "@toddle-edu/ds-web";
import { TickCircleFilled } from "@toddle-edu/ds-icons";

import { useMount, useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils";
import { getProjectDeadlinesDetailsText } from "Projects/modules/Utils";

const PopoverList = ({ data, onEscape, onClosePopover }) => {
  const containerRef = useRef({});

  const setRef = useCallback(node => {
    containerRef.current = node;
  }, []);

  useMount(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const handleScroll = event => {
      if (containerRef?.current) {
        if (
          !(
            event.target === containerRef?.current ||
            event.target.contains(containerRef?.current)
          )
        ) {
          onClosePopover();
        }
      }
    };

    const handleKeyDown = event => {
      if (event.key === "Escape" && onEscape) {
        onEscape(event);
      }
    };

    document.addEventListener("scroll", handleScroll, {
      capture: true,
      signal,
    });
    document.addEventListener("keydown", handleKeyDown, {
      capture: true,
      signal,
    });

    return () => {
      controller.abort();
    };
  }, [onClosePopover, onEscape]);

  return (
    <div className={classes.listContainer} ref={setRef}>
      <List data={data} />
    </div>
  );
};

const DeadlinesPopover = props => {
  const {
    disabled,
    isLoading,
    children,
    title = "title",
    itemsList,
    popoverType,
  } = props;
  const { t } = useI18n(["common", "project"]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const onClickHandler = () => {
    setIsPopoverOpen(state => !state);
  };

  const onClosePopover = () => {
    setIsPopoverOpen(false);
  };

  const handleClick = e => {
    e.stopPropagation();
    if (!disabled && !isLoading) {
      onClickHandler();
    }
  };

  const handleEscape = e => {
    e.stopPropagation();
    if (e.key === "Escape") {
      onClosePopover();
    }
  };

  const handleKeyDownOnPopoverTrigger = useCallback(e => {
    onEnterOrSpaceKeyDown(e, onClickHandler);
  }, []);

  const listOptions = useMemo(() => {
    switch (popoverType) {
      case "deadline": {
        return _.map(itemsList, student => {
          const { name, profileImage, id } = student;
          return {
            id,
            value: name,
            prefix: (
              <Avatar
                src={profileImage}
                name={name}
                id={id}
                dsVersion="2.0"
                shape="circle"
              />
            ),
          };
        });
      }
      case "dashboard": {
        return _.map(itemsList, item => {
          const { isComplete, dueOn, closesOn, id, title } = item;
          const {
            dueDateSubtext,
            closeDateSubtext,
          } = getProjectDeadlinesDetailsText({ dueOn, closesOn, t });
          const prefix = isComplete ? (
            <Tooltip tooltip={t("common:completed")}>
              <TickCircleFilled variant="success" />
            </Tooltip>
          ) : (
            <Tooltip tooltip={t("common:pending")}>
              <div className={classes.checkboxOff}></div>
            </Tooltip>
          );

          const value = (
            <div className={classes.titleDateContainer}>
              <Tooltip tooltip={title}>
                <div className={classes.title} dir="auto">
                  {title}
                </div>
              </Tooltip>
              <div className={classNames("text-body-s", classes.dateContainer)}>
                <div>{dueDateSubtext}</div>
                <div className={classes.dot}></div>
                <div>{closeDateSubtext}</div>
              </div>
            </div>
          );

          return {
            id,
            value,
            prefix,
          };
        });
      }
    }
  }, [itemsList, popoverType]);

  return (
    <div
      tabIndex={0}
      onClick={e => {
        e.stopPropagation();
      }}
      onKeyDown={handleKeyDownOnPopoverTrigger}
    >
      <Popover
        containerClassName={classes.popoverContainer}
        isOpen={isPopoverOpen}
        onClickOutside={onClosePopover}
        onCloseButtonClick={onClosePopover}
        title={title}
        body={
          isPopoverOpen ? (
            <PopoverList
              data={listOptions}
              onEscape={handleEscape}
              onClosePopover={onClosePopover}
            />
          ) : (
            <div></div>
          )
        }
      >
        <div onClick={handleClick}>{children}</div>
      </Popover>
    </div>
  );
};

export default DeadlinesPopover;
